<!-- Repeated Booking Form -->
<form [formGroup]="modalFormRepeatedBooking()!">
  @if (variant() === 'chips') {
    <!-- The basket (#1138): the basket's own cards and buttons, the design system's chips. The
         repeats start from the period's own day, so only the last day is chosen; the basket shows
         the time being repeated above. -->
    <div class="repeat-basket">
      <app-checkout-card>
        <h4 class="repeat-basket__title">Gentag tiden</h4>
        <div class="repeat-chips">
          <abook-filter-select
            icon="event_repeat"
            label="Ugedag"
            multiple
            required
            requiredText="Vælg ugedag"
            [allowAll]="false"
            [options]="weekdayOptions"
            [value]="formValue().weekday"
            (valueChange)="setWeekdays($event)"
          />
          <abook-filter-select
            icon="repeat"
            label="Gentag"
            [allowAll]="false"
            [options]="intervalOptions"
            [value]="formValue().interval"
            [changed]="formValue().interval !== 'w1'"
            (valueChange)="setInterval($event)"
            (cleared)="setInterval('w1')"
          />
          <abook-date-chip
            label="Gentag til"
            icon="event"
            [min]="formValue().start"
            [value]="formValue().end"
            (valueChange)="setEnd($event)"
          />
        </div>
      </app-checkout-card>

      @if (searchResults().length > 0) {
        <app-checkout-card>
          <h4 class="repeat-basket__title">Datoer</h4>
          <div class="date-groups">
            @if (freePeriods().length) {
              <button
                type="button"
                class="date-group"
                [class.date-group--open]="showFree()"
                [attr.aria-expanded]="showFree()"
                (click)="showFree.set(!showFree())"
              >
                <mat-icon class="date-group__icon" aria-hidden="true">event_available</mat-icon>
                <span class="date-group__text">
                  <strong>{{ choosableCount() === 1 ? '1 ledig dato' : choosableCount() + ' ledige datoer' }}</strong>
                  <span>{{ freeSummary() }} · {{ showFree() ? 'Skjul datoer' : 'Vis datoer' }}</span>
                </span>
                <mat-icon class="date-group__chevron" aria-hidden="true">{{ showFree() ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
              @if (showFree()) {
                <mat-selection-list class="date-list" (selectionChange)="onPeriodsSelectionChange($event)">
                  @for (period of freePeriods(); track period) {
                    <mat-list-option
                      [selected]="period.isSelected || !!period.isSource"
                      [value]="period"
                      [disabled]="isPeriodDisabled(period)"
                    >
                      <span class="date-list__when"
                        >{{ period.start | danishDate: 'weekdayDateTime' }}-{{ period.end | danishDate: 'time' }}</span
                      >
                      @if (period.isSource) {
                        <span class="date-list__source">Den valgte tid</span>
                      } @else {
                        @if (!period.price && showMissingPrice()) {
                          <span class="date-list__warn">Ingen pris fundet</span>
                        }
                        @if (showBookerTypeWarnings() && period.bookerTypeWarning) {
                          <span class="date-list__warn">{{ period.bookerTypeWarning }}</span>
                        }
                        @if ((period.overQuota || overQuota().has(period)) && period.maxBookingTime; as quota) {
                          <span class="date-list__warn">
                            Over maksimumtiden med seriens andre datoer ({{ quotaText(quota.maxMinutes, quota.period) }})
                          </span>
                        }
                      }
                    </mat-list-option>
                  }
                </mat-selection-list>
              }
            }
            @if (takenPeriods().length) {
              <button
                type="button"
                class="date-group"
                [class.date-group--open]="showTaken()"
                [attr.aria-expanded]="showTaken()"
                (click)="showTaken.set(!showTaken())"
              >
                <mat-icon class="date-group__icon" aria-hidden="true">event_busy</mat-icon>
                <span class="date-group__text">
                  <strong>{{ takenPeriods().length === 1 ? '1 dato er ikke ledig' : takenPeriods().length + ' datoer er ikke ledige' }}</strong>
                  <span>Kommer ikke med i kurven · {{ showTaken() ? 'Skjul datoer' : 'Vis datoer' }}</span>
                </span>
                <mat-icon class="date-group__chevron" aria-hidden="true">{{ showTaken() ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
              @if (showTaken()) {
                <ul class="date-list date-list--taken">
                  @for (period of takenPeriods(); track period) {
                    <li>
                      <span class="date-list__taken">
                        <span class="date-list__when"
                          >{{ period.start | danishDate: 'weekdayDateTime' }}-{{ period.end | danishDate: 'time' }}</span
                        >
                        <!-- Why it cannot go in, as the check says it (Max 24-10-2026: not only on hover). -->
                        <span class="date-list__reason">{{ takenReason(period) }}</span>
                      </span>
                      <mat-icon class="date-list__why" aria-hidden="true">event_busy</mat-icon>
                    </li>
                  }
                </ul>
              }
            }
          </div>
        </app-checkout-card>
        <abook-button
          size="large"
          type="filled"
          class="repeat-basket__action"
          [isLoading]="isSubmitting()"
          [disabled]="!hasSelectedPeriods() || overQuota().size > 0"
          (clickFn)="onSubmit()"
        >
          {{ selectedCount() === 1 ? 'Læg 1 tid i kurv' : 'Læg ' + selectedCount() + ' tider i kurv' }}
        </abook-button>
      } @else {
        <abook-button
          size="large"
          type="filled"
          class="repeat-basket__action"
          [disabled]="!modalFormRepeatedBooking()!.valid"
          [isLoading]="isSearching()"
          (clickFn)="onSearchAvailableDates()"
        >
          Find datoer
        </abook-button>
      }
    </div>
  } @else {
    <div class="container-fluid mt-3">
      <!-- Selected Period Card -->
      <mat-card>
        <h5>Gentagne bookinger</h5>
        @if (selectedPeriod()) {
          <div>
            <b>
              @if (selectedPeriod()?.activity) {
                <span>{{ selectedPeriod()?.activity.name }}</span>
              }
              ({{ selectedPeriod()?.resource?.name }})
            </b>
            <br />
            {{ $safeNavigationMigration(selectedPeriod()?.start) | danishDate: 'weekdayDateTime' }}-{{ $safeNavigationMigration(selectedPeriod()?.end) | danishDate: 'time' }}
          </div>
        }
        <hr />

        <!-- Weekday Selection -->
        <mat-form-field class="w-100">
          <mat-label>Vælg ugedag</mat-label>
          <mat-select placeholder="Vælg ugedag" formControlName="weekday" multiple required>
            @for (option of weekdayOptions; track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <!-- How often -->
        <mat-form-field class="w-100">
          <mat-label>Gentag</mat-label>
          <mat-select formControlName="interval">
            @for (option of intervalOptions; track option.value) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <!-- Date Range Selection -->
        <mat-form-field class="w-100">
          <mat-label>Vælg periode</mat-label>
          <mat-date-range-input [rangePicker]="picker" required>
            <input matInput matStartDate placeholder="Start date" formControlName="start" required />
            <input matInput matEndDate placeholder="End date" formControlName="end" required />
          </mat-date-range-input>
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-date-range-picker #picker></mat-date-range-picker>
        </mat-form-field>

        <!-- Action Buttons -->
        <div class="actions --end">
          <abook-button type="outlined" class="me-3" (clickFn)="handleClose()"> Luk </abook-button>
          <abook-button
            [type]="searchButtonType()"
            (clickFn)="onSearchAvailableDates()"
            [disabled]="!modalFormRepeatedBooking()!.valid"
            [isLoading]="isSearching()"
          >
            Søg tilgængelige perioder
          </abook-button>
        </div>
      </mat-card>

      <!-- Available Periods Card -->
      @if (searchResults().length > 0) {
        <mat-card class="card-repeatable-available-periods">
          <mat-card-header>
            <h5>Tilgængelige perioder</h5>
          </mat-card-header>
          <mat-card-content>
            <ng-container [ngTemplateOutlet]="periodList" />

            <!-- Action Buttons -->
            <div class="actions">
              <abook-button type="outlined" (clickFn)="handleClose()" [disabled]="isSubmitting()"> Luk </abook-button>
              <abook-button
                type="filled"
                [isLoading]="isSubmitting()"
                (clickFn)="onSubmit()"
                [disabled]="!hasSelectedPeriods() || overQuota().size > 0"
              >
                Tilføj
              </abook-button>
            </div>
          </mat-card-content>
        </mat-card>
      }
    </div>
  }
</form>

<!-- The dates found, each with what stands in its way — the same list in both variants. -->
<ng-template #periodList>
  <mat-selection-list class="w-100" (selectionChange)="onPeriodsSelectionChange($event)">
    @for (period of searchResults(); track period) {
      <!-- The time being repeated shows ticked and greyed: it is in the series, and already added. -->
      <mat-list-option
        [selected]="period.isSelected || !!period.isSource"
        [value]="period"
        class="multiline-mat-option"
        [disabled]="isPeriodDisabled(period)"
      >
        <div>
          <span class="period-when"
            >{{ period.start | danishDate: 'weekdayDateTime' }}-{{ period.end | danishDate: 'time' }}</span
          >
          @if (period.price) {
            <span
              >. Pris: <b>{{ period.price | currency: 'DKK' : 'symbol' }}</b></span
            >
          }
          <div class="period-badges">
            @if (period.isSource) {
              <span class="badge bg-secondary">Den valgte tid</span>
            } @else {
              @if (period.isDisabled) {
                <span class="badge bg-warning">Overlapper en eksisterende periode</span>
              }
              @if (!period.price && showMissingPrice()) {
                <span class="badge bg-warning">Ingen pris fundet</span>
              }
              @if (period.message) {
                <span class="badge bg-warning">{{ period.message }}</span>
              }
              @if (showBookerTypeWarnings() && period.bookerTypeWarning) {
                <span class="badge bg-warning booker-type-warning">{{ period.bookerTypeWarning }}</span>
              }
              @if ((period.overQuota || overQuota().has(period)) && period.maxBookingTime; as quota) {
                <span class="badge bg-warning">
                  Over maksimumtiden med seriens andre datoer ({{ quotaText(quota.maxMinutes, quota.period) }})
                </span>
              }
            }
          </div>
        </div>
      </mat-list-option>
    }
  </mat-selection-list>
</ng-template>
